/**
 * RMWAssignment — Risk Worksheet assignment manager.
 *
 * This single entry page renders one of three role-specific views:
 *
 * 1. **Admin / HR / Process Compliance Manager** — full management surface:
 *    a DataGrid of every project with columns for assigned PM, current cycle,
 *    overall risk level, and actions to Enable RMW or Reassign PM. Two
 *    dialogs (`Enable RMS` and `Reassign PM`) call `riskEnableUrl` and
 *    `riskAssignPMUrl` respectively. The reassign dialog also surfaces the
 *    immutable assignment history fetched from the worksheet detail
 *    endpoint.
 * 2. **Manager (acting as PM)** — list of worksheets where the user is the
 *    current `assigned_pm`, each row linking to the editable workspace.
 * 3. **Employee** — list of projects the user is a team member of, each row
 *    linking to the read-only project risk summary page.
 *
 * Process Compliance Officer is redirected to the org dashboard.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData, postData } from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import CustomNoRowsOverlay from "../../../components/Data/NoRowsOverlayComponent";
import {
  projectsUrl,
  riskAssignPMUrl,
  riskEnableUrl,
  riskTemplatesUrl,
  riskWorksheetDetailUrl,
  riskWorksheetsUrl,
  viewActiveUsers,
} from "../../../Service/Apis";
import {
  RiskReviewFrequency,
  RiskTemplate,
  RiskWorksheet,
  RiskWorksheetAssignment,
} from "./Types/RiskManagementTypes";
import RiskScoreBadge from "./components/RiskScoreBadge";
import { isRmwAdmin, isRmwEmployee, isRmwPM } from "./components/rmwRoles";
import {
  normalizeAssignment,
  normalizeWorksheet,
  normalizeWorksheetList,
} from "./components/normalizers";

interface ProjectOption {
  project_id: string;
  project_name: string;
  project_key?: string;
  team_id?: string;
  team_name?: string;
  project_manager?: string | null;
  manager_name?: string | null;
}

interface UserOption {
  id: string;
  first_name?: string;
  last_name?: string;
  email: string;
  roles?: Array<{ role: string } | string>;
}

const FREQUENCY_OPTIONS: { value: RiskReviewFrequency; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "custom", label: "Custom" },
];

/** Pretty-print a user record into "First Last <email>". */
const userLabel = (u: UserOption | null | undefined): string => {
  if (!u) return "";
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name ? `${name} (${u.email})` : u.email;
};

const hasRole = (u: UserOption, role: string): boolean => {
  const wanted = role.toLowerCase();
  return (u.roles ?? []).some((r) => {
    const value = typeof r === "string" ? r : r.role;
    return value?.toLowerCase() === wanted;
  });
};

const RMWAssignment: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );
  const userId = useSelector((state: RootState) => state.auth.userId);

  // Process Compliance Officer is a view-only viewer of org-wide pages,
  // not the assignment page.
  useEffect(() => {
    if (selectedRole?.toLowerCase() === "process compliance officer") {
      navigate("/pms/risk-management/dashboard", { replace: true });
    }
  }, [selectedRole, navigate]);

  const isAdmin = isRmwAdmin(selectedRole);
  const isPM = isRmwPM(selectedRole);
  const isEmp = isRmwEmployee(selectedRole);

  const [worksheets, setWorksheets] = useState<RiskWorksheet[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [templates, setTemplates] = useState<RiskTemplate[]>([]);
  const [managers, setManagers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<"all" | "enabled" | "disabled">(
    "all"
  );
  const [filterPM, setFilterPM] = useState<string>("");
  const [filterSearch, setFilterSearch] = useState<string>("");

  // Enable RMW dialog
  const [enableOpen, setEnableOpen] = useState(false);
  const [enableProject, setEnableProject] = useState<ProjectOption | null>(null);
  const [enablePM, setEnablePM] = useState<UserOption | null>(null);
  const [enableTemplate, setEnableTemplate] = useState<string>("");
  const [enableFrequency, setEnableFrequency] =
    useState<RiskReviewFrequency>("monthly");
  const [enableSaving, setEnableSaving] = useState(false);
  const [enableError, setEnableError] = useState("");

  // Reassign PM dialog
  const [reassignTarget, setReassignTarget] = useState<RiskWorksheet | null>(null);
  const [reassignPM, setReassignPM] = useState<UserOption | null>(null);
  const [reassignReason, setReassignReason] = useState("");
  const [reassignHistory, setReassignHistory] = useState<RiskWorksheetAssignment[]>(
    []
  );
  const [reassignSaving, setReassignSaving] = useState(false);
  const [reassignError, setReassignError] = useState("");

  const fetchWorksheets = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedRole) params.set("role", selectedRole);
    if (filterStatus !== "all") {
      params.set("is_active", filterStatus === "enabled" ? "true" : "false");
    }
    if (filterPM) params.set("pm", filterPM);
    if (filterSearch) params.set("search", filterSearch);
    // For the PM ("Manager") view, restrict to worksheets the user is the
    // assigned PM on. The backend's baseline scope for a Manager also
    // includes worksheets they belong to as a *team member* (so they can
    // still load them via direct URL), so we explicitly intersect with the
    // `pm` filter here — otherwise the "My Risk Worksheets" list would
    // include team-member-only projects too. The backend query param is
    // `pm`, NOT `assigned_pm`; this used to send the wrong key so the
    // filter was silently ignored.
    if (isPM && userId) params.set("pm", userId);
    dispatch(fetchData(`${riskWorksheetsUrl}?${params.toString()}`))
      .then((res: any) => setWorksheets(normalizeWorksheetList(res)))
      .catch(() => setWorksheets([]))
      .finally(() => setLoading(false));
  }, [dispatch, selectedRole, filterStatus, filterPM, filterSearch, isPM, userId]);

  const fetchProjects = useCallback(() => {
    if (!selectedRole) return;
    dispatch(fetchData(`${projectsUrl}?role=${selectedRole}`))
      .then((res: any) => {
        const list: ProjectOption[] = res?.results ?? res ?? [];
        setProjects(
          [...list].sort((a, b) =>
            (a.project_name ?? "").localeCompare(b.project_name ?? "", undefined, {
              sensitivity: "base",
            })
          )
        );
      })
      .catch(() => setProjects([]));
  }, [dispatch, selectedRole]);

  const fetchTemplates = useCallback(() => {
    dispatch(fetchData(`${riskTemplatesUrl}?page_size=100`))
      .then((res: any) => {
        const list: RiskTemplate[] = res?.results ?? res ?? [];
        setTemplates(list);
        // Default to the first template flagged is_default, else first one.
        if (list.length && !enableTemplate) {
          const def = list.find((t) => t.is_default) ?? list[0];
          setEnableTemplate(def.id);
        }
      })
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const fetchManagers = useCallback(() => {
    dispatch(fetchData(`${viewActiveUsers}?page_size=500`))
      .then((res: any) => {
        const list: UserOption[] = res?.results ?? res ?? [];
        // Only users carrying a Manager role can be assigned as the PM.
        setManagers(list.filter((u) => hasRole(u, "Manager")));
      })
      .catch(() => setManagers([]));
  }, [dispatch]);

  useEffect(() => {
    fetchWorksheets();
    // `/jira/projects/` is only needed by the admin/HR/PCM table (it powers
    // the "Enable RMS" picker and lets admins see every project regardless of
    // whether a worksheet exists yet). PMs and employees both render off the
    // worksheets endpoint, which already applies the correct user scope.
    if (isAdmin) {
      fetchProjects();
      fetchTemplates();
      fetchManagers();
    }
  }, [fetchWorksheets, fetchProjects, fetchTemplates, fetchManagers, isAdmin]);

  // -------------------------------------------------------------------------
  // Enable RMW
  // -------------------------------------------------------------------------

  const openEnable = (project?: ProjectOption) => {
    setEnableProject(project ?? null);
    setEnablePM(null);
    setEnableFrequency("monthly");
    setEnableError("");
    setEnableOpen(true);
  };

  const handleEnable = async () => {
    if (!enableProject) {
      setEnableError("Please choose a project.");
      return;
    }
    if (!enablePM) {
      setEnableError("Please choose the Project Manager who will own the worksheet.");
      return;
    }
    if (!enableTemplate) {
      setEnableError("Please choose a template.");
      return;
    }
    setEnableSaving(true);
    try {
      // Single POST to the list-level `enable` action. The backend creates
      // (or reactivates) the worksheet, inserts the initial PM assignment
      // row, and opens the first review cycle in one atomic transaction.
      await dispatch(
        postData(riskEnableUrl, {
          project: enableProject.project_id,
          template: enableTemplate,
          review_frequency: enableFrequency,
          pm: enablePM.id,
          open_first_cycle: true,
        })
      );
      showToast("Risk Management enabled for project.", "success", false);
      setEnableOpen(false);
      fetchWorksheets();
    } catch (err: any) {
      const data = err?.response?.data;
      setEnableError(
        data?.detail ||
          data?.non_field_errors?.[0] ||
          data?.pm?.[0] ||
          data?.project?.[0] ||
          data?.template?.[0] ||
          "Failed to enable Risk Management. Please try again."
      );
    } finally {
      setEnableSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Reassign PM
  // -------------------------------------------------------------------------

  const openReassign = async (ws: RiskWorksheet) => {
    setReassignTarget(ws);
    setReassignPM(null);
    setReassignReason("");
    setReassignError("");
    setReassignHistory(ws.assignment_history ?? []);
    try {
      const res = await dispatch(fetchData(riskWorksheetDetailUrl(ws.id)));
      const detail = normalizeWorksheet(res);
      setReassignTarget(detail);
      if (detail?.assignment_history) {
        setReassignHistory(detail.assignment_history.map(normalizeAssignment));
      }
    } catch {
      /* keep what we already had */
    }
  };

  const handleReassign = async () => {
    if (!reassignTarget || !reassignPM) {
      setReassignError("Please choose a new Project Manager.");
      return;
    }
    setReassignSaving(true);
    try {
      await dispatch(
        postData(riskAssignPMUrl(reassignTarget.id), {
          // Backend serializer keys: `assigned_pm` + optional `note`.
          assigned_pm: reassignPM.id,
          note: reassignReason || "",
        })
      );
      showToast("Project Manager reassigned.", "success", false);
      setReassignTarget(null);
      fetchWorksheets();
    } catch (err: any) {
      const data = err?.response?.data;
      setReassignError(
        data?.detail ||
          data?.non_field_errors?.[0] ||
          data?.assigned_pm?.[0] ||
          data?.pm?.[0] ||
          "Failed to reassign PM. Please try again."
      );
    } finally {
      setReassignSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Derived rows
  // -------------------------------------------------------------------------

  /** Worksheet rows keyed by `project_id` for the DataGrid. */
  const wsByProject = useMemo(() => {
    const map = new Map<string, RiskWorksheet>();
    worksheets.forEach((w) => map.set(w.project, w));
    return map;
  }, [worksheets]);

  const adminRows = useMemo(() => {
    return projects
      .filter((p) =>
        !filterSearch
          ? true
          : p.project_name?.toLowerCase().includes(filterSearch.toLowerCase())
      )
      .map((p) => {
        const ws = wsByProject.get(p.project_id);
        return {
          id: p.project_id,
          project_id: p.project_id,
          project_name: p.project_name,
          team_name: p.team_name ?? "—",
          worksheet_id: ws?.id ?? null,
          assigned_pm_name:
            ws?.current_pm_name ?? p.manager_name ?? "Unassigned",
          frequency: ws?.review_frequency ?? null,
          status: ws?.is_active ? "Enabled" : "Disabled",
          level: ws?.current_overall_level ?? null,
          exposure: ws?.current_overall_exposure ?? null,
          last_reviewed: ws?.last_reviewed_at ?? null,
          worksheet: ws ?? null,
        };
      });
  }, [projects, wsByProject, filterSearch]);

  const pmRows = useMemo(() => {
    return worksheets.map((w) => ({
      ...w,
      id: w.id,
    }));
  }, [worksheets]);

  // Employee rows are derived directly from `worksheets`, NOT from `/jira/
  // projects/`. The projects endpoint only matches an employee's single
  // `user.team`, missing projects the user belongs to via other team
  // memberships. The RMW worksheet endpoint already applies the broader
  // employee scope (`project__team_id__team__employee_id=user`) so it returns
  // every worksheet the employee is allowed to view — which is exactly what
  // this page is meant to surface.
  const employeeRows = useMemo(() => {
    return worksheets
      .filter((w) =>
        !filterSearch
          ? true
          : w.project_name?.toLowerCase().includes(filterSearch.toLowerCase())
      )
      .map((w) => ({
        id: w.id,
        project_id: w.project,
        project_name: w.project_name ?? "—",
        team_name: w.team_name ?? "—",
        worksheet_id: w.id,
        assigned_pm_name: w.current_pm_name ?? "Unassigned",
        status: w.is_active ? "Enabled" : "Disabled",
        level: w.current_overall_level ?? null,
        exposure: w.current_overall_exposure ?? null,
      }));
  }, [worksheets, filterSearch]);

  // -------------------------------------------------------------------------
  // DataGrid column defs
  // -------------------------------------------------------------------------

  const adminColumns: GridColDef[] = [
    { field: "project_name", headerName: "Project", flex: 1.4, minWidth: 220 },
    { field: "team_name", headerName: "Business Unit / Team", flex: 1, minWidth: 160 },
    { field: "assigned_pm_name", headerName: "Assigned PM", flex: 1, minWidth: 180 },
    {
      field: "frequency",
      headerName: "Frequency",
      width: 120,
      renderCell: (p: GridRenderCellParams) =>
        p.value ? (
          <Chip size="small" label={String(p.value).toUpperCase()} />
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        ),
    },
    {
      field: "level",
      headerName: "Overall Risk",
      width: 130,
      renderCell: (p: GridRenderCellParams) => (
        <RiskScoreBadge level={p.value as any} exposure={p.row.exposure} />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          size="small"
          label={p.value as string}
          color={p.value === "Enabled" ? "success" : "default"}
          variant={p.value === "Enabled" ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "last_reviewed",
      headerName: "Last Reviewed",
      width: 140,
      valueFormatter: ({ value }) =>
        value ? new Date(value as string).toLocaleDateString() : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 260,
      renderCell: (p: GridRenderCellParams) => {
        const ws: RiskWorksheet | null = p.row.worksheet;
        if (!ws) {
          return (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => openEnable({
                project_id: p.row.project_id,
                project_name: p.row.project_name,
                team_name: p.row.team_name,
              })}
            >
              Enable RMS
            </Button>
          );
        }
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Open worksheet">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/pms/risk-management/workspace/${ws.id}`)
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Project summary">
              <IconButton
                size="small"
                onClick={() =>
                  navigate(`/pms/risk-management/summary/${ws.project}`)
                }
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reassign PM">
              <IconButton size="small" onClick={() => openReassign(ws)}>
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const pmColumns: GridColDef[] = [
    { field: "project_name", headerName: "Project", flex: 1.5, minWidth: 220 },
    {
      field: "current_cycle_name",
      headerName: "Current Cycle",
      flex: 1,
      minWidth: 160,
      renderCell: (p) =>
        p.value ? (
          <Stack>
            <Typography variant="body2">{String(p.value)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {p.row.current_cycle_status === "submitted"
                ? "open · submitted"
                : p.row.current_cycle_status ?? "—"}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled">No open cycle</Typography>
        ),
    },
    {
      field: "current_overall_level",
      headerName: "Overall Risk",
      width: 140,
      renderCell: (p) => (
        <RiskScoreBadge
          level={p.value as any}
          exposure={p.row.current_overall_exposure}
        />
      ),
    },
    {
      field: "risk_count",
      headerName: "Risks",
      width: 100,
      valueGetter: (p) => p.row.risk_count ?? 0,
    },
    {
      field: "last_reviewed_at",
      headerName: "Last Reviewed",
      width: 140,
      valueFormatter: ({ value }) =>
        value ? new Date(value as string).toLocaleDateString() : "—",
    },
    {
      field: "actions",
      headerName: "",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/pms/risk-management/workspace/${p.row.id}`)}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              navigate(`/pms/risk-management/summary/${p.row.project}`)
            }
          >
            Summary
          </Button>
        </Stack>
      ),
    },
  ];

  const employeeColumns: GridColDef[] = [
    { field: "project_name", headerName: "Project", flex: 1.5, minWidth: 220 },
    { field: "team_name", headerName: "Business Unit / Team", flex: 1, minWidth: 160 },
    { field: "assigned_pm_name", headerName: "Project Manager", flex: 1, minWidth: 180 },
    {
      field: "level",
      headerName: "Overall Risk",
      width: 140,
      renderCell: (p) => (
        <RiskScoreBadge level={p.value as any} exposure={p.row.exposure} />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value as string}
          color={p.value === "Enabled" ? "success" : "default"}
          variant={p.value === "Enabled" ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        p.row.worksheet_id ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() =>
              navigate(`/pms/risk-management/summary/${p.row.project_id}`)
            }
          >
            View Summary
          </Button>
        ) : (
          <Typography variant="caption" color="text.disabled">
            RMW not enabled
          </Typography>
        ),
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const renderAdminFilters = () => (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Search project"
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(e.target.value as "all" | "enabled" | "disabled")
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="enabled">Enabled</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={5} md={3}>
          <Autocomplete
            size="small"
            options={managers}
            getOptionLabel={userLabel}
            value={managers.find((m) => m.id === filterPM) ?? null}
            onChange={(_e, v) => setFilterPM(v?.id ?? "")}
            renderInput={(params) => (
              <TextField {...params} label="Filter by PM" />
            )}
          />
        </Grid>
        <Grid item xs={12} md sx={{ textAlign: { md: "right" } }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => openEnable()}
          >
            Enable RMS on Project
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderAdminView = () => {
    const enabledCount = worksheets.filter((w) => w.is_active).length;
    const highCount = worksheets.filter(
      (w) => w.current_overall_level === "HIGH"
    ).length;
    return (
      <>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "Total Projects", value: projects.length, color: "#1976d2" },
            { label: "RMW Enabled", value: enabledCount, color: "#2e7d32" },
            {
              label: "Not Enabled",
              value: Math.max(projects.length - enabledCount, 0),
              color: "#ed6c02",
            },
            { label: "High Risk", value: highCount, color: "#c62828" },
          ].map((s) => (
            <Grid key={s.label} item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center", py: 1.5 }}>
                  <Typography variant="h4" fontWeight="bold" color={s.color}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {s.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {renderAdminFilters()}

        <Paper elevation={1} sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={adminRows}
            columns={adminColumns}
            loading={loading}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            initialState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </>
    );
  };

  const renderPMView = () => (
    <Paper elevation={1} sx={{ height: 600, width: "100%" }}>
      <DataGrid
        rows={pmRows}
        columns={pmColumns}
        loading={loading}
        disableRowSelectionOnClick
        slots={{ noRowsOverlay: CustomNoRowsOverlay }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  const renderEmployeeView = () => (
    <Paper elevation={1} sx={{ height: 600, width: "100%" }}>
      <DataGrid
        rows={employeeRows}
        columns={employeeColumns}
        loading={loading}
        disableRowSelectionOnClick
        slots={{ noRowsOverlay: CustomNoRowsOverlay }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>
        {isAdmin
          ? "Risk Management — Assignments"
          : isPM
          ? "My Risk Worksheets"
          : "Project Risk Summary"}
      </PageTitle>

      {!isAdmin && !isPM && !isEmp && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You do not have a Risk Management role on this page. Please switch
          roles to view the assignment grid.
        </Alert>
      )}

      {isAdmin && renderAdminView()}
      {isPM && renderPMView()}
      {isEmp && renderEmployeeView()}

      {/* -------------------- Enable RMS dialog -------------------- */}
      <Dialog
        open={enableOpen}
        onClose={() => !enableSaving && setEnableOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Enable Risk Management
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setEnableOpen(false)} disabled={enableSaving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {enableError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {enableError}
            </Alert>
          )}
          <Stack spacing={2}>
            <Autocomplete
              options={projects.filter((p) => !wsByProject.has(p.project_id))}
              getOptionLabel={(p) => p.project_name ?? ""}
              value={enableProject}
              onChange={(_e, v) => setEnableProject(v)}
              renderInput={(params) => (
                <TextField {...params} label="Project" required />
              )}
            />
            <Autocomplete
              options={managers}
              getOptionLabel={userLabel}
              value={enablePM}
              onChange={(_e, v) => setEnablePM(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Manager"
                  required
                  helperText="Only users with the Manager role can be assigned."
                />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Review Frequency</InputLabel>
              <Select
                label="Review Frequency"
                value={enableFrequency}
                onChange={(e) =>
                  setEnableFrequency(e.target.value as RiskReviewFrequency)
                }
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Risk Template</InputLabel>
              <Select
                label="Risk Template"
                value={enableTemplate}
                onChange={(e) => setEnableTemplate(e.target.value as string)}
              >
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                    {t.is_default ? " (default)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnableOpen(false)} disabled={enableSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEnable}
            disabled={enableSaving}
            startIcon={enableSaving ? <CircularProgress size={16} /> : null}
          >
            Enable RMS
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------- Reassign PM dialog -------------------- */}
      <Dialog
        open={!!reassignTarget}
        onClose={() => !reassignSaving && setReassignTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Reassign Project Manager
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setReassignTarget(null)}
            disabled={reassignSaving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {reassignError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reassignError}
            </Alert>
          )}
          {reassignTarget && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                Project
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {reassignTarget.project_name ?? reassignTarget.project}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Current PM:{" "}
                <b>{reassignTarget.current_pm_name ?? "Unassigned"}</b>
              </Typography>

              <Stack spacing={2}>
                <Autocomplete
                  options={managers}
                  getOptionLabel={userLabel}
                  value={reassignPM}
                  onChange={(_e, v) => setReassignPM(v)}
                  renderInput={(params) => (
                    <TextField {...params} label="New Project Manager" required />
                  )}
                />
                <TextField
                  label="Reason (optional)"
                  multiline
                  minRows={2}
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                />
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography
                variant="subtitle1"
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <HistoryIcon fontSize="small" />
                Assignment history
              </Typography>
              {reassignHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No previous assignments recorded.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {reassignHistory.map((h) => (
                    <Paper
                      key={h.id}
                      variant="outlined"
                      sx={{ p: 1.5, bgcolor: h.is_current ? "action.hover" : "inherit" }}
                    >
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          label={h.is_current ? "CURRENT" : "PREVIOUS"}
                          color={h.is_current ? "success" : "default"}
                        />
                        <Typography variant="body2">
                          <b>{h.assigned_pm_name ?? h.assigned_pm}</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          assigned by{" "}
                          {h.assigned_by_name ?? h.assigned_by} on{" "}
                          {new Date(h.assigned_at).toLocaleString()}
                        </Typography>
                      </Stack>
                      {h.reason && (
                        <Typography variant="caption" color="text.secondary">
                          Reason: {h.reason}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReassignTarget(null)}
            disabled={reassignSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReassign}
            disabled={reassignSaving}
            startIcon={reassignSaving ? <CircularProgress size={16} /> : null}
          >
            Reassign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RMWAssignment;
